/*
 * BuenaVida homepage popup controller.
 *
 * Instance-per-element with a real destroy(), so the theme editor can add / move / delete the
 * section without leaking a timer that later fires open() on a detached node.
 *
 * Ownership (the cross-fire fix): the popup and the footer newsletter are BOTH
 * `{% form 'customer' %}` on the homepage, and `form.posted_successfully?` is page-scoped per form
 * type — so the footer's success used to force this popup open with the discount code, and this
 * popup's success used to print the footer's "thanks" message. Each form now carries its own
 * `return_to` token (?bv_ok=popup | ?bv_ok=footer) and only celebrates when the token is its own.
 *
 * The frequency cap is written on confirmed success or an explicit close — NEVER on submit.
 * Writing it on submit meant a server-rejected signup silently suppressed the popup for 14 days.
 */
(function () {
  'use strict';

  var CAP_KEY = 'bv_popup_expiry';
  var OWNER_KEY = 'bv_form_owner'; // written by assets/bv-forms.js on submit
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var instances = new WeakMap();

  function readOwner() {
    try {
      return sessionStorage.getItem(OWNER_KEY);
    } catch (e) {
      return null;
    }
  }

  function clearOwner() {
    try {
      sessionStorage.removeItem(OWNER_KEY);
    } catch (e) {
      /* private mode */
    }
  }

  function Popup(root) {
    this.root = root;
    this.card = root.querySelector('[data-bv-card]');
    this.body = root.querySelector('[data-bv-scroll]');
    this.form = root.querySelector('.bv-popup__form');
    this.stepForm = root.querySelector('[data-bv-step="form"]');
    this.stepSuccess = root.querySelector('[data-bv-step="success"]');
    this.serverError = root.querySelector('[data-bv-server-error]');
    this.delay = (parseInt(root.getAttribute('data-delay'), 10) || 5) * 1000;
    this.capDays = parseInt(root.getAttribute('data-cookie-days'), 10) || 14;
    this.timer = null;
    this.lastFocus = null;
    this.wasLocked = false;
    this.listeners = [];

    this.bind();
    this.decideInitialState();
  }

  Popup.prototype.on = function (target, type, handler, opts) {
    target.addEventListener(type, handler, opts);
    this.listeners.push([target, type, handler, opts]);
  };

  /* ── storage ───────────────────────────────────────────────────────────── */

  Popup.prototype.setCap = function () {
    try {
      localStorage.setItem(CAP_KEY, String(Date.now() + this.capDays * 86400000));
    } catch (e) {
      /* private mode — the popup simply shows again next visit */
    }
  };

  Popup.prototype.suppressed = function () {
    try {
      var until = localStorage.getItem(CAP_KEY);
      return !!until && Date.now() < parseInt(until, 10);
    } catch (e) {
      return false;
    }
  };

  /* ── open / close ──────────────────────────────────────────────────────── */

  Popup.prototype.focusables = function () {
    return Array.prototype.filter.call(this.card.querySelectorAll(FOCUSABLE), function (el) {
      // offsetParent is null for the hidden step and for anything display:none
      return el.offsetParent !== null || el === document.activeElement;
    });
  };

  Popup.prototype.open = function () {
    if (this.root.classList.contains('is-open')) return;
    this.lastFocus = document.activeElement;
    this.root.hidden = false;

    var self = this;
    // two frames: let the browser lay the card out (and decode the image) before transitioning
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.root.classList.add('is-open');
      });
    });

    // Use the theme's own lock class (cart-drawer.js / details-modal.js / global.js all do),
    // and do not steal it if the cart drawer already owns it.
    this.wasLocked = document.body.classList.contains('overflow-hidden');
    if (!this.wasLocked) document.body.classList.add('overflow-hidden');

    this.syncViewport();

    var f = this.focusables();
    (f[0] || this.card).focus({ preventScroll: true });
  };

  Popup.prototype.close = function (options) {
    if (!this.root.classList.contains('is-open')) return;
    var cap = !options || options.cap !== false;

    this.root.classList.remove('is-open');
    if (!this.wasLocked) document.body.classList.remove('overflow-hidden');
    this.releaseViewport();

    var self = this;
    this.hideTimer = setTimeout(function () {
      self.root.hidden = true;
    }, 300);

    if (cap) this.setCap();
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus({ preventScroll: true });
    }
  };

  Popup.prototype.showStep = function (name) {
    if (!this.stepForm || !this.stepSuccess) return;
    var success = name === 'success';
    this.stepForm.hidden = success;
    this.stepSuccess.hidden = !success;
    if (success) {
      var heading = this.stepSuccess.querySelector('.bv-popup__title');
      if (heading && heading.id) this.card.setAttribute('aria-labelledby', heading.id);
    }
  };

  /* ── viewport (iOS keyboard + URL bar) ─────────────────────────────────── */

  /* Pin the overlay to the VISUAL viewport rather than the layout viewport. The card is
   * max-height:100% of this padded overlay, so shrinking the overlay shrinks the card and its
   * padding is preserved automatically — no height arithmetic to get wrong.
   * This matters on iOS: dvh tracks the URL bar but does NOT shrink when the keyboard opens,
   * so without it the submit button ends up underneath the keyboard. */
  Popup.prototype.syncViewport = function () {
    var vv = window.visualViewport;
    if (!vv) return;
    var root = this.root;
    var apply = function () {
      root.style.height = vv.height + 'px';
      root.style.top = vv.offsetTop + 'px';
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    this._vvRelease = function () {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      root.style.removeProperty('height');
      root.style.removeProperty('top');
    };
  };

  Popup.prototype.releaseViewport = function () {
    if (this._vvRelease) {
      this._vvRelease();
      this._vvRelease = null;
    }
  };

  /* ── events ────────────────────────────────────────────────────────────── */

  Popup.prototype.bind = function () {
    var self = this;

    this.on(this.root, 'click', function (e) {
      if (e.target.closest('[data-bv-popup-close]')) {
        self.close();
        return;
      }
      if (e.target.closest('[data-bv-copy]')) {
        self.copyCode(e.target.closest('[data-bv-copy]'));
        return;
      }
      if (!e.target.closest('[data-bv-card]')) self.close();
    });

    this.on(document, 'keydown', function (e) {
      if (!self.root.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        self.close();
        return;
      }
      if (e.key !== 'Tab') return;
      var f = self.focusables();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Backstop: the trap above only patches the Tab edges. Focus can still land outside the
    // card (browser chrome, programmatic focus, screen-reader navigation) — pull it back.
    this.on(document, 'focusin', function (e) {
      if (!self.root.classList.contains('is-open')) return;
      if (self.card.contains(e.target)) return;
      var f = self.focusables();
      (f[0] || self.card).focus({ preventScroll: true });
    });

    // Keep the focused field visible when the iOS keyboard covers the lower half.
    if (this.body) {
      this.on(this.body, 'focusin', function (e) {
        if (!e.target.matches('input, textarea, select')) return;
        setTimeout(function () {
          e.target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 250);
      });
    }

    if (this.form) {
      this.on(this.form, 'submit', function (e) {
        // bv-forms.js runs its bot guard in the capture phase and may already have cancelled.
        if (e.defaultPrevented) return;

        var email = self.form.querySelector('input[type="email"]');
        var error = self.form.querySelector('[data-bv-error]');
        if (email && !/.+@.+\..+/.test(email.value.trim())) {
          e.preventDefault();
          if (error) error.hidden = false;
          email.setAttribute('aria-invalid', 'true');
          email.focus();
          return;
        }
        if (error) error.hidden = true;
        if (email) email.removeAttribute('aria-invalid');

        self.composePhoneTag();

        // Ownership is recorded by assets/bv-forms.js from this form's data-bv-owner attribute.
        // Deliberately NO setCap() here: writing the frequency cap on submit is what used to
        // suppress the popup for 14 days after a submit Shopify then rejected.
      });
    }
  };

  /* ── phone → customer tag ──────────────────────────────────────────────────
   * `contact[note][Teléfono]` was never a field Shopify's customer form persists, so every
   * number entered since launch was silently discarded. The input is deliberately named
   * `bv_phone` (outside the contact[…] namespace) so it is never POSTed on its own; we fold a
   * normalised copy into contact[tags], which the customer record does keep and which the
   * merchant can read in Admin → Customers → Tags, or search as `tag:tel-…`.
   */
  Popup.prototype.composePhoneTag = function () {
    var input = this.form.querySelector('[data-bv-phone]');
    var tags = this.form.querySelector('[data-bv-tags]');
    if (!input || !tags) return;

    var digits = (input.value || '').replace(/\D/g, '');
    if (!digits) return;
    if (digits.length === 9) digits = '51' + digits; // Peru mobile entered without country code
    if (digits.length < 8 || digits.length > 15) return; // E.164 sanity — never tag junk

    tags.value = tags.value + ',tel-' + digits;
  };

  /* ── copy code ─────────────────────────────────────────────────────────── */

  Popup.prototype.copyCode = function (button) {
    var code = this.root.querySelector('[data-bv-code]');
    if (!code) return;
    var text = code.textContent.trim();
    var done = function () {
      var original = button.textContent;
      var copied = button.getAttribute('data-bv-copied');
      if (copied) {
        button.textContent = copied;
        setTimeout(function () {
          button.textContent = original;
        }, 2000);
      }
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, function () {});
      return;
    }
    try {
      var sel = window.getSelection();
      var range = document.createRange();
      range.selectNodeContents(code);
      sel.removeAllRanges();
      sel.addRange(range);
      document.execCommand('copy');
      sel.removeAllRanges();
      done();
    } catch (e) {
      /* selection API unavailable — the code is on screen and selectable by hand */
    }
  };

  /* ── initial state ─────────────────────────────────────────────────────── */

  Popup.prototype.decideInitialState = function () {
    // Who actually submitted? bv-forms.js records this on submit. `posted` below is only
    // "SOME customer form on this page was posted" — it is true for the footer's submit too.
    var owner = readOwner();
    var posted = !!this.root.querySelector('[data-bv-customer-posted]');

    if (posted && owner === 'popup') {
      // WE submitted and Shopify accepted it. Reveal the code and cap.
      this.showStep('success');
      this.open();
      this.setCap();
      clearOwner();
      return;
    }

    if (this.serverError && owner === 'popup') {
      // WE submitted and Shopify rejected it. Re-open on the form step, show why, and do NOT cap
      // — capping a failed submit is what used to hide the popup for 14 days after one error.
      this.showStep('form');
      this.open();
      var el = this.serverError;
      setTimeout(function () {
        el.focus();
      }, 350);
      clearOwner();
      return;
    }

    if (posted || this.serverError) {
      // Another form on this page owns this submit (the footer newsletter). Stay shut. The cap and
      // the delay carry on as normal, so this visitor still gets their own chance later.
      clearOwner();
      return;
    }

    var designMode = !!(window.Shopify && window.Shopify.designMode);
    if (!this.suppressed() || designMode) {
      var self = this;
      this.timer = setTimeout(function () {
        self.open();
      }, this.delay);
    }
  };

  /* ── teardown ──────────────────────────────────────────────────────────── */

  Popup.prototype.destroy = function () {
    clearTimeout(this.timer);
    clearTimeout(this.hideTimer);
    this.releaseViewport();
    if (!this.wasLocked) document.body.classList.remove('overflow-hidden');
    for (var i = 0; i < this.listeners.length; i++) {
      var l = this.listeners[i];
      l[0].removeEventListener(l[1], l[2], l[3]);
    }
    this.listeners.length = 0;
  };

  /* ── lifecycle ─────────────────────────────────────────────────────────── */

  function initAll(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll ? root.querySelectorAll('[data-bv-popup]') : [];
    Array.prototype.forEach.call(nodes, function (el) {
      if (!instances.has(el)) instances.set(el, new Popup(el));
    });
  }

  function destroyIn(scope) {
    if (!scope || !scope.querySelectorAll) return;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-bv-popup]'), function (el) {
      var p = instances.get(el);
      if (p) {
        p.destroy();
        instances.delete(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    destroyIn(e.target);
  });
  document.addEventListener('shopify:section:select', function (e) {
    var el = e.target.querySelector && e.target.querySelector('[data-bv-popup]');
    var p = el && instances.get(el);
    if (p) p.open();
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    var el = e.target.querySelector && e.target.querySelector('[data-bv-popup]');
    var p = el && instances.get(el);
    if (p) p.close({ cap: false });
  });
})();
